import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import fetch from 'node-fetch'
import { brand } from '../../../redux/app-selectors'
import { Field, Label, Radio, RadioGroup, Input, Button, Select, FieldGroup } from '../../../atoms'
import { FsCore } from 'types/fs-core'
import DirectorFinder, { DirectorDetails } from '../form-helpers/director-details'
import { useForm } from 'react-hook-form'
import { setConfig } from '../../helpers/consent'
import { fetchWithRetry } from '../../../utils/fetch-with-retry'
import Image from '../../imgix-img'
import { useComponentTracking } from '../../../utils/component-events'
import HomeAddress from '../form-helpers/home-address'
import uuid from '../../../utils/guid'
import classNames from 'classnames'
import ProgressCircle from '../../form/progress-circle'
import { readCookieRaw } from '../../../utils/cookies'
import ProductDetails from '../../product-details'
import { getComponentById } from '../../../redux/component/component-selectors'
import ProfileInformation from '../form-helpers/profile-information'
import getImageHost from '../../../utils/get-image-host'
import { maskPII } from '../../../utils/form'
import { NumberInput } from '../form-helpers/number-input'
import { fetchProfile, piiData, prefillFormData } from '../form-helpers/utils'
import Prefill from '../form-helpers/prefill'

interface SMEzemplerFormProps {
  brand: 'money' | 'uswitch' | 'confused'
  sys: {
    id: string
  }
  name: string
}

interface TermsAndConditionsData {
  url: string
  tCsReferenceNumber: number
}

const debounce: Record<string, number> = {}

// This form replicates this zempler journey:
// https://application.cashplus.com/forms/businessapplication/businessFormStep1/CPCorpDefault
const SMEzemplerForm: React.FC<SMEzemplerFormProps> = ({ brand, productData, schema }) => {
  const [businessDirectors, setBusinessDirectors] = useState<DirectorDetails[]>([])
  const [termsAndConditions, setTermsAndConditions] = useState<TermsAndConditionsData | null>()
  const [industryOptions, setIndustryOptions] = useState<object[] | []>([])
  const [validationError, setValidationError] = useState<string | null>(null)
  const [currentIndustryName, setCurrentIndustryName] = useState<string | null>(null)
  const [termsAndConditionsError, settermsAndConditionsError] = useState<string | null>(null)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [showBusinessDetailsSection, setShowBusinessDetailsSection] = React.useState(false)
  const [consents, setConsents] = useState({ marketing: true })
  const [prefilled, setPrefilled] = React.useState({ prefilled: false, loading: false })

  const { __FS_API_URL__ } = global
  const apiPath: string = __FS_API_URL__ || 'https://www.money.co.uk/api'
  const formName = 'Zempler form'

  const fetchTerms = async () => {
    try {
      settermsAndConditionsError(null)
      const response = await fetchWithRetry(`${apiPath}/zempler/terms-and-conditions/176`)
      if (response === undefined || !response.ok) {
        settermsAndConditionsError('Oops looks like something went wrong. Please try again later')
      } else {
        const data: TermsAndConditionsData = await response.json()
        setTermsAndConditions(data)
      }
    } catch (e) {
      settermsAndConditionsError('Oops looks like something went wrong. Please try again later')
    }
  }

  const fetchIndustries = async () => {
    try {
      const response = await fetch(`${apiPath}/zempler/industries`)
      const data = await response.json()
      const industryList = data.map((d) => {
        return {
          text: d.name,
          value: d.industryId,
        }
      })

      setIndustryOptions([{ text: 'Please Select', value: '' }, ...industryList])
    } catch (e) {}
  }

  useEffect(() => {
    setConfig({
      apiPath: window.location.href.includes('staging')
        ? 'https://www.staging.money.co.uk/consent'
        : 'https://www.money.co.uk/consent',
      clientId: 'fs-core',
      brand: 'money',
    })
    fetchTerms()
    fetchIndustries()
  }, [])

  const trackComponent: (
    eventName: string,
    subEventName: string,
    payload: Record<string, any>,
  ) => void = useComponentTracking()

  const trackFormFieldInteracted = (detail: string, value: any) => {
    return trackComponent('Form Event', 'formFieldInteracted', {
      formStep: detail,
      selected: piiData.includes(detail) ? 'untracked' : value,
    })
  }
  const {
    register,
    handleSubmit,
    errors,
    setValue,
    watch,
    unregister,
    control,
    setError,
    clearErrors,
  }: { register: (validation: any) => any } = useForm({
    mode: 'onChange',
    defaultValues: {
      businessDetails: {
        businessType: 'limitedCompany',
      },
      directorDetails: {
        title: undefined,
        firstName: undefined,
        lastName: undefined,
        dateOfBirth: undefined,
        emailAddress: undefined,
        mobileNumber: undefined,
        nationality: undefined,
      },
      otherDetails: {
        fscsConsent: false,
        productConsent: false,
        marketingConsent: false,
        isSameHomeAddress: 'yes',
      },
    },
  })

  const businessName = watch('businessDetails.businessName')
  const directorFirstName = watch('directorDetails.firstName')
  const directorLastName = watch('directorDetails.lastName')
  const companyNumber = watch('businessDetails.companyNumber')
  const businessType = watch('businessDetails.businessType')
  const industryId = watch('businessDetails.industryId')
  const individualPSCs = watch('businessDetails.activePSCs')?.filter(
    (psc) => psc.kind === 'individual-person-with-significant-control',
  )
  const card = watch('otherDetails.businessNameOnCard')
  const isSameHomeAddress = watch('otherDetails.isSameHomeAddress')
  const directorDobDay = watch('directorDetails.dateOfBirthFields.day')
  const nonDirectorPSCs = individualPSCs?.reduce((acc, curr) => {
    if (
      !(
        curr.name_elements.forename === directorFirstName &&
        curr.name_elements.surname === directorLastName
      )
    ) {
      return acc.concat(curr)
    } else {
      return acc
    }
  }, [])
  const showPSCDetails =
    individualPSCs?.length > 0 && directorFirstName && nonDirectorPSCs.length > 0

  const registerWithTrack = (validation: any, detail: string) => {
    return register({
      ...validation,
      validate: (value: any) => {
        clearTimeout(debounce[detail])
        debounce[detail] = setTimeout(() => {
          trackFormFieldInteracted(detail, value)
        }, 1000)
        return true
      },
    })
  }

  useEffect(() => {
    const smeSession = readCookieRaw('sme-session-id')
    if (smeSession) {
      setPrefilled({ loading: true, prefilled: false })
      fetchProfile()
        .then((profileData) => {
          prefillFormData(profileData, setValue, register, trackFormFieldInteracted).then(() => {
            setPrefilled({ loading: false, prefilled: true })
            setShowBusinessDetailsSection(true)
            trackComponent('Form Event', 'prefillInfo', {
              FormName: formName,
              FieldData: maskPII(
                { ...profileData.businessDetails, ...profileData.directorDetails },
                piiData,
              ),
            })
          })
        })
        .catch((e) => {
          setPrefilled({ loading: false, prefilled: false })
          // eslint-disable-next-line no-console
          console.error(e.message)
        })
    }
  }, [])

  useEffect(() => {
    const fetchDirectorData = async () => {
      const res = await fetch(`/api/company/${companyNumber}/officers`)
      const json = await res.json()
      const directors = json.data.filter((d) => d.officer_role === 'director')
      setBusinessDirectors(directors)
    }
    if (companyNumber && businessType !== 'soleTrader') {
      fetchDirectorData()
    }
  }, [companyNumber])

  useEffect(() => {
    if (businessName) {
      setValue('otherDetails.businessNameOnCard', businessName)
    }
    if (businessType !== 'soleTrader') {
      unregister('businessDetails.industryId')
    }
  }, [businessName])

  const handleBusinessDetailsSubmit = async (finalBody: Object) => {
    const res = await fetch(
      `${apiPath}/sme-profile?brand=money&deviceId=${readCookieRaw('uscd')}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalBody),
      },
    )
    trackComponent('Form Event', 'sectionCompleted', {
      SectionName: 'Profile Information',
      FormName: formName,
      FieldData: maskPII(finalBody, ['businessAddress', 'email']),
      status: res.ok ? 'Success' : 'Failed',
    })
    setShowBusinessDetailsSection(true)
  }

  return (
    <div className='ProviderForm'>
      <div className='SMEform'>
        <div>
          <h3>Apply for a Zempler current account</h3>
          <div className='SMEform__logo'>
            <Image
              src={`https://${getImageHost()}/s3/uswitch-assets-eu/amp/images/provider/logos/Zempler_Logo_Stacked_RGB_DarkBlue.png`}
              width={180}
              height={66}
              critical={false}
              alt={'Zempler Logo'}
            />
          </div>
          {(prefilled.loading || prefilled.prefilled) && <Prefill loading={prefilled.loading} />}
          {termsAndConditionsError ? (
            <div className='ProviderForm__termsAndConditionsError'>{termsAndConditionsError}</div>
          ) : (
            <>
              <form onSubmit={(e) => e.preventDefault()}>
                <ProfileInformation
                  registerWithTrack={registerWithTrack}
                  register={register}
                  setValue={setValue}
                  unregister={unregister}
                  businessDetails={watch('businessDetails')}
                  businessOptions={[
                    { name: 'limitedCompany', text: 'Limited Company' },
                    { name: 'soleTrader', text: 'Sole Trader' },
                    { name: 'other', text: 'Other (e.g. Partnership)' },
                  ]}
                  directorDetails={watch('directorDetails')}
                  hideButton={showBusinessDetailsSection}
                  consents={consents}
                  setConsents={setConsents}
                  setValidationError={setValidationError}
                  setError={setError}
                  clearErrors={clearErrors}
                  errors={errors}
                  brand={brand}
                  consentSource={'zempler-journey'}
                  formName={formName}
                  ctaText='Continue to business details'
                  handleSubmit={handleBusinessDetailsSubmit}
                  control={control}
                  defaultBusinessNameInput={watch('businessNameInput')}
                />
                {businessType !== 'other' && (
                  <>
                    {showBusinessDetailsSection ? (
                      <>
                        <div className='SMEform__companyDetails__detailsBox'>
                          <div className='SMEform__title__box'>
                            <Label className='SMEform__title'>
                              {businessType === 'limitedCompany'
                                ? 'Business details'
                                : 'Additional information'}
                            </Label>
                          </div>
                          <div className='SMEform__input'>
                            <Field>
                              <Label>Select the best type that describes your business</Label>
                              <div className='SMEform__input'>
                                <Select
                                  name='businessDetails.industryId'
                                  options={industryOptions}
                                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                    const industryName =
                                      e.target.options[e.target.selectedIndex].text
                                    setValue('businessDetails.industryId', e.target.value)
                                    setCurrentIndustryName(industryName)
                                  }}
                                  value={industryId}
                                  ref={registerWithTrack(
                                    {
                                      required: 'Please select your industry',
                                    },
                                    'industryId',
                                  )}
                                />
                                {errors?.businessDetails?.industryId && (
                                  <div className='SMEform__error'>
                                    {errors.businessDetails.industryId.message}
                                  </div>
                                )}
                              </div>
                            </Field>
                            {industryId && industryId === '14' && (
                              <Field>
                                <Label className='required'>Tell us your industry</Label>
                                <input
                                  name='businessDetails.industryName'
                                  type='text'
                                  value={watch('businessDetails.industryName') ?? ''}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setValue('businessDetails.industryName', e.target.value)
                                  }}
                                  ref={registerWithTrack(
                                    {
                                      required: 'Please enter your industry name',
                                      minLength: {
                                        value: 3,
                                        message: 'Minimum 3 character industry',
                                      },
                                      maxLength: {
                                        value: 50,
                                        message: 'Maximum 50 character industry',
                                      },
                                      pattern: {
                                        value: /^[a-zA-Z \-']+$/,
                                        message: 'Please enter a valid industry',
                                      },
                                    },
                                    'industryName',
                                  )}
                                />
                                {errors?.businessDetails?.industryName && (
                                  <div className='SMEform__error'>
                                    {errors.businessDetails.industryName.message}
                                  </div>
                                )}
                              </Field>
                            )}
                            <Field>
                              <Label className='required'>Number of employees</Label>
                              <input
                                name='businessDetails.numberOfEmployees'
                                type='number'
                                value={watch('businessDetails.numberOfEmployees') ?? ''}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  setValue('businessDetails.numberOfEmployees', e.target.value)
                                }}
                                ref={registerWithTrack(
                                  {
                                    required:
                                      'Please enter the Number of employees in your business',
                                    min: {
                                      value: 1,
                                      message: 'Minimum of 1 employee required',
                                    },
                                  },
                                  'numberOfEmployees',
                                )}
                              />
                              {errors?.businessDetails?.numberOfEmployees && (
                                <div className='SMEform__error'>
                                  {errors.businessDetails.numberOfEmployees.message}
                                </div>
                              )}
                            </Field>
                            <Field>
                              <Label
                                className={classNames({
                                  required: businessType === 'limitedCompany',
                                })}
                              >
                                What is your annual turnover?
                              </Label>
                              <NumberInput
                                name='businessDetails.turnover'
                                control={control}
                                rules={{
                                  required: {
                                    value: true,
                                    message: 'Please enter the annual turnover of your business',
                                  },
                                }}
                                trackField={(name: string, value: string) =>
                                  trackComponent('Form Event', 'formFieldInteracted', {
                                    formStep: name,
                                    selected: value,
                                  })
                                }
                                defaultValue={watch('businessDetails.turnover')}
                              />
                              {errors?.businessDetails?.turnover && (
                                <div className='SMEform__error'>
                                  {errors.businessDetails.turnover.message}
                                </div>
                              )}
                            </Field>
                            <Field>
                              <Label className='required'>
                                Business name as it should appear on card
                              </Label>
                              <input
                                value={card ?? businessName ?? ''}
                                name='otherDetails.businessNameOnCard'
                                type='text'
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  setValue('otherDetails.businessNameOnCard', e.target.value)
                                }}
                                ref={registerWithTrack(
                                  {
                                    required:
                                      'Please enter the name you would like on your bank card',
                                    maxLength: {
                                      value: 21,
                                      message: 'Maximum 21 characters',
                                    },
                                    pattern: {
                                      // eslint-disable-next-line no-useless-escape
                                      value: /^[a-zA-Z0-9][a-zA-Z0-9 -&.,'\/()]+$/,
                                      message:
                                        "Please enter the company name exactly as you'd like it to appear on your card using only letters, numbers, spaces and these special characters ',.&. The first character must be a letter or number and you're limited to 21 characters.",
                                    },
                                  },
                                  'businessNameOnCard',
                                )}
                              />
                              {errors?.otherDetails?.businessNameOnCard && (
                                <div className='SMEform__error'>
                                  {errors.otherDetails.businessNameOnCard.message}
                                </div>
                              )}
                              <p>
                                Please enter the company name exactly as you&apos;d like it to
                                appear on your card
                              </p>
                            </Field>
                          </div>
                        </div>
                        <DirectorFinder
                          directorDetails={watch('directorDetails', {})}
                          businessDirectors={businessDirectors}
                          registerWithTrack={registerWithTrack}
                          register={register}
                          setValue={setValue}
                          errors={errors}
                          setError={setError}
                          clearErrors={clearErrors}
                          over18Check={true}
                        />
                        {directorFirstName && (
                          <Field className='SMEform__typeOfBusiness'>
                            <Label>Is your Business address the same as your home address?</Label>
                            <div style={{ marginBottom: '12px' }}>
                              This is where Zempler will send your card and letters
                            </div>
                            <div className='SMEform__input'>
                              <RadioGroup column={false}>
                                <Radio
                                  name='otherDetails.isSameHomeAddress'
                                  value='yes'
                                  currentValue={watch('otherDetails.isSameHomeAddress')}
                                  onChange={(e) => {
                                    setValue(`otherDetails.isSameHomeAddress`, e.target.value)
                                  }}
                                  ref={registerWithTrack(
                                    {
                                      required:
                                        'Please tell us if your home address is the same as your business address',
                                    },
                                    'isSameHomeAddress',
                                  )}
                                >
                                  Yes
                                </Radio>
                                <Radio
                                  name='otherDetails.isSameHomeAddress'
                                  value='no'
                                  currentValue={watch('otherDetails.isSameHomeAddress')}
                                  onChange={(e) => {
                                    setValue(`otherDetails.isSameHomeAddress`, e.target.value)
                                  }}
                                  ref={registerWithTrack(
                                    {
                                      required:
                                        'Please tell us if your home address is the same as your business address',
                                    },
                                    'isSameHomeAddress',
                                  )}
                                >
                                  No
                                </Radio>
                              </RadioGroup>
                              {errors?.businessDetails?.isSameHomeAddress && (
                                <div className='SMEform__error'>
                                  {errors?.businessDetails?.isSameHomeAddress.message}
                                </div>
                              )}
                            </div>
                          </Field>
                        )}
                        {isSameHomeAddress === 'no' && (
                          <div className='SMEform__directorDetails__detailsBox'>
                            <HomeAddress
                              register={register}
                              errors={errors}
                              setValue={setValue}
                              setError={setError}
                              clearErrors={clearErrors}
                              addressFieldName={'directorDetails.address'}
                            />
                          </div>
                        )}

                        {individualPSCs && directorFirstName && (
                          <div
                            className={classNames('SMEform__directorDetails__detailsBox', {
                              'SMEform__directorDetails__detailsBox--hide': !showPSCDetails,
                            })}
                          >
                            <div className='SMEform__title'>Additional Information</div>
                            <div className='SMEform__title--subTitle'>
                              Zempler will run checks on all new accounts. Please complete the
                              information below for all persons of significant control
                            </div>
                            {individualPSCs.map((psc, key) => {
                              const isDirector =
                                psc.name_elements.forename === directorFirstName &&
                                psc.name_elements.surname === directorLastName
                              return (
                                <div
                                  key={key}
                                  className={classNames('SMEform__PSCDetails', {
                                    'SMEform__PSCDetails--hide': isDirector,
                                  })}
                                >
                                  <div className='SMEform__title'>{psc.name}</div>
                                  <Field>
                                    <Label className='required'>Date of Birth</Label>
                                    <div className='SMEform__input'>
                                      <FieldGroup>
                                        <input
                                          placeholder='DD'
                                          value={
                                            isDirector
                                              ? directorDobDay
                                              : psc.date_of_birth.day ?? ''
                                          }
                                          name={`businessDetails.activePSCs[${key}].date_of_birth.day`}
                                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            setValue(
                                              `businessDetails.activePSCs[${key}].date_of_birth.day`,
                                              e.target.value,
                                            )
                                          }}
                                          ref={registerWithTrack(
                                            {
                                              required: {
                                                value: true,
                                                message: 'Please enter your day of birth',
                                              },
                                              min: {
                                                value: 1,
                                                message: 'Day must be between 1 and 31',
                                              },
                                              max: {
                                                value: 31,
                                                message: 'Day must be between 1 and 31',
                                              },
                                              pattern: {
                                                value: /^\d+$/,
                                                message: 'Day must be a valid number',
                                              },
                                            },
                                            'dateOfBirthFields.day',
                                          )}
                                        />
                                        <input
                                          placeholder='MM'
                                          value={psc.date_of_birth.month ?? ''}
                                          name='directorDetails.dateOfBirthFields.month'
                                          disabled
                                        />
                                        <input
                                          placeholder='YYYY'
                                          value={psc.date_of_birth.year ?? ''}
                                          name='directorDetails.dateOfBirthFields.year'
                                          disabled
                                        />
                                      </FieldGroup>
                                    </div>
                                    {errors.businessDetails?.activePSCs?.[key]?.date_of_birth
                                      ?.day && (
                                      <div className='SMEform__error'>
                                        {
                                          errors.businessDetails.activePSCs[key].date_of_birth.day
                                            .message
                                        }
                                      </div>
                                    )}
                                  </Field>
                                  <HomeAddress
                                    addressFieldName={`businessDetails.activePSCs[${key}].address`}
                                    register={register}
                                    errors={errors}
                                    setValue={setValue}
                                    setError={setError}
                                    clearErrors={clearErrors}
                                    defaultAddress={
                                      isDirector
                                        ? watch('directorDetails.address') ??
                                          watch('businessDetails.address')
                                        : undefined
                                    }
                                  />
                                </div>
                              )
                            })}
                          </div>
                        )}

                        {businessName && businessType !== 'other' && (
                          <>
                            <div className='SMEform__productDetailsMobile'>
                              <ProductDetails productData={productData} schema={schema} />
                            </div>

                            <Field className='SMEform__typeOfBusiness'>
                              <Label>Review Terms</Label>
                              <div className='SMEform__input'>
                                <label className='SMEform__fscsConsent'>
                                  <Input
                                    type='checkbox'
                                    name='otherDetails.fscsConsent'
                                    ref={registerWithTrack(
                                      {
                                        required: 'Please review and aknowledge the terms',
                                      },
                                      'fscsConsent',
                                    )}
                                  />
                                  <div>
                                    I acknowledge the cover of the{' '}
                                    <a href='https://www.zemplerbank.com/fscs' target='blank'>
                                      Financial Services Compensation Scheme
                                    </a>
                                  </div>
                                </label>
                                {termsAndConditions?.url && businessType !== 'other' && (
                                  <label className='SMEform__fscsConsent'>
                                    <Input
                                      type='checkbox'
                                      name='otherDetails.productConsent'
                                      ref={registerWithTrack(
                                        {
                                          required: 'Please review and accept the product terms',
                                        },
                                        'productConsent',
                                      )}
                                    />
                                    <div>
                                      I have read and accepted the current{' '}
                                      <a href={termsAndConditions?.url} target='blank'>
                                        Terms and Conditions
                                      </a>{' '}
                                      and agree to apply for a Zempler Business Bank Account
                                    </div>
                                  </label>
                                )}
                                {businessType !== 'other' && (
                                  <div className='SMEform__fscsConsent'>
                                    <p>
                                      Only a director, or persons with permission of a director, can
                                      submit this application. By clicking ‘I agree to apply for a
                                      Zempler Business Bank Account’ you agree to the above Terms
                                      and Conditions. As part of the application, Zempler Bank will
                                      need to verify your identity which will involve Zempler Bank
                                      checking your personal details against third party databases.
                                    </p>
                                  </div>
                                )}

                                {businessType !== 'other' && (
                                  <div className='SMEform__fscsConsent'>
                                    <p>
                                      I consent to my details and those of my company to be passed
                                      to the partners for the purpose of opening and providing the
                                      services, and to being contacted directly by the partners for
                                      the purposes of fulfilling the request for those services.
                                    </p>
                                  </div>
                                )}
                                {(errors?.otherDetails?.productConsent ||
                                  errors?.otherDetails?.fscsConsent) && (
                                  <div className='SMEform__error'>
                                    Please tick all the checkboxes above
                                  </div>
                                )}
                                <div className='SMEform__fscsConsent'>
                                  <p>
                                    We will show you a range of products from our partners. View
                                    their{' '}
                                    <a
                                      href='https://www.money.co.uk/privacy-notice/partners'
                                      target='blank'
                                    >
                                      privacy policies
                                    </a>{' '}
                                    for more information.
                                  </p>
                                </div>
                              </div>
                            </Field>
                          </>
                        )}
                        {validationError && (
                          <div className='SMEform__validationError'>{validationError}</div>
                        )}

                        {businessType !== 'other' && (
                          <div className='SMEform__buttonWrapper'>
                            {submitLoading ? (
                              <div className='SMEform__loadingMessage'>
                                <div className='SMEform__loadingMessage__spinner'>
                                  <ProgressCircle height={48} />
                                </div>
                                <p>
                                  Thanks for your patience, it should only take a minute or two.
                                  Zempler&apos;s just checking everything you&apos;ve provided.
                                  Please don&apos;t hit refresh or back
                                </p>
                              </div>
                            ) : (
                              <Button
                                variant='primary'
                                size='lg'
                                type='submit'
                                onClick={handleSubmit(async (data) => {
                                  try {
                                    setSubmitLoading(true)
                                    setValidationError(null)
                                    const { businessDetails, directorDetails, otherDetails } = data
                                    const finalBody = {
                                      businessType: businessDetails.businessType,
                                      businessName: businessDetails.businessName,
                                      activePSCs: businessDetails.activePSCs,
                                      numberOfEmployees: parseInt(
                                        businessDetails.numberOfEmployees,
                                      ),
                                      turnover: parseInt(businessDetails.turnover),
                                      businessNameOnCard: otherDetails.businessNameOnCard,
                                      industryId: businessDetails.industryId,
                                      industryName:
                                        currentIndustryName !== 'Other service'
                                          ? currentIndustryName
                                          : businessDetails.industryName,
                                      sic_code: businessDetails.sic_code,
                                      date_of_creation: businessDetails.date_of_creation,
                                      dateOfCreation: businessDetails.dateOfCreation,
                                      tCsReferenceNumber: termsAndConditions?.tCsReferenceNumber,
                                      title: directorDetails.title,
                                      firstName: directorDetails.firstName,
                                      lastName: directorDetails.lastName,
                                      dateOfBirthYear: directorDetails.dateOfBirthFields.year,
                                      dateOfBirthMonth:
                                        directorDetails.dateOfBirthFields.month.replace(
                                          /^(?:0+(?=[1-9])|0+(?=0$))/gm,
                                          '',
                                        ),
                                      dateOfBirthDay: directorDetails.dateOfBirthFields.day.replace(
                                        /^(?:0+(?=[1-9])|0+(?=0$))/gm,
                                        '',
                                      ),
                                      emailAddress: directorDetails.emailAddress,
                                      mobileNumber: directorDetails.mobileNumber,
                                      workNumber: directorDetails.mobileNumber,
                                      homeAddress: directorDetails.address?.postcode
                                        ? directorDetails.address
                                        : businessDetails.address,
                                      nationality: directorDetails.nationality,
                                      companyRegistrationNumber: businessDetails.companyNumber,
                                      registeredBusinessAddress: businessDetails.address,
                                      fscsAcknowledged: true,
                                    }
                                    trackComponent('Form Event', 'sectionCompleted', {
                                      SectionName: 'Application Form Information',
                                      FormName: formName,
                                      FieldData: maskPII(finalBody, [
                                        'registeredBusinessAddress',
                                        'emailAddress',
                                        'homeAddress',
                                        'firstName',
                                        'lastName',
                                        'dateOfBirthDay',
                                        'dateOfBirthMonth',
                                        'dateOfBirthYear',
                                        'title',
                                        'mobileNumber',
                                        'workNumber',
                                        'nationality',
                                      ]),
                                    })
                                    trackComponent('Form Event', 'formSubmitted', {
                                      formName: formName,
                                    })
                                    const res = await fetch(
                                      `${apiPath}/zempler/application?brand=money&requestId=${uuid()}&deviceId=${readCookieRaw(
                                        'uscd',
                                      )}`,
                                      {
                                        method: 'POST',
                                        body: JSON.stringify(finalBody),
                                        headers: {
                                          'Content-Type': 'application/json',
                                        },
                                      },
                                    )
                                    if (res.ok) {
                                      const res = await fetchWithRetry(
                                        `${apiPath}/zempler/application?brand=money&check=true&deviceId=${readCookieRaw(
                                          'uscd',
                                        )}`,
                                        60000,
                                        500,
                                      )
                                      if (res === undefined || !res.ok) {
                                        setValidationError(
                                          `Oops looks like something went wrong. Timeout Error`,
                                        )
                                        setSubmitLoading(false)
                                      } else {
                                        const response = await res.json()
                                        if (response.status !== 'Error') {
                                          window.location.href = '/business/zempler/results'
                                        } else {
                                          setValidationError(
                                            'Oops looks like something went wrong.',
                                          )
                                          setSubmitLoading(false)
                                        }
                                      }
                                    } else {
                                      const result = await res.json()
                                      if (result.errors && Array.isArray(result.errors)) {
                                        setValidationError(
                                          `Oops looks like something went wrong. ${JSON.stringify(
                                            result.errors?.[0]?.message ?? result.errors,
                                          )}`,
                                        )
                                      } else {
                                        setValidationError(
                                          `Oops looks like something went wrong. ${JSON.stringify(
                                            result,
                                          )}`,
                                        )
                                      }
                                      setSubmitLoading(false)
                                    }
                                  } catch (e) {
                                    setValidationError(
                                      `Oops looks like something went wrong. Please try again later. ${e}`,
                                    )
                                    setSubmitLoading(false)
                                  }
                                })}
                              >
                                Submit
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className='SMEform__companyDetails__detailsBox'>
                          <div className='SMEform__title'>Business details</div>
                        </div>
                        <div className='SMEform__companyDetails__detailsBox'>
                          <div className='SMEform__title'>
                            {businessType === 'limitedCompany'
                              ? `Director's details`
                              : `Personal details`}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </form>

              {businessType === 'other' && (
                <div className='ProviderForm__notSupported'>
                  <div className='ProviderForm__notSupported__text'>
                    Unfortunately our current journey does not support businesses other than Sole
                    Traders or limited companies
                  </div>
                  <div className='ProviderForm__notSupported__text'>
                    Click the button below to apply via the Zempler website
                  </div>
                  <Button
                    variant='primary'
                    size='lg'
                    href='https://www.money.co.uk/clk/1ffe7e07-8515-4b37-bda7-efd1ca2f3f28-30a4ba001fba5299338318b61da2ca8fba7216df/money/business-current-accounts?r=https%3A%2F%2Fwww.cashplus.com%2Flanding%2Fbusiness%2Fbusiness-deluxe-nmf-muk%3Fpc%3DBUS088%26ClickRef%3D%7B%7Bclick_ref%7D%7D&category=business-current-accounts&supplier=Zempler&product_name=Zempler+Bank+formerly+Cashplus+Business+Account&pid=1ffe7e07-8515-4b37-bda7-efd1ca2f3f28&ln=default&upvid=1ffe7e07-8515-4b37-bda7-efd1ca2f3f28-30a4ba001fba5299338318b61da2ca8fba7216df&ucvid=1000031447&ulvid=955908&link_id=452d0e78-a960-4b30-a727-581f1fc94006&sig=96e3fa292b04d4f44bb1f6a8beb7707a191c5bff0b5e518458f8523f7e93143a'
                  >
                    Go to Zempler
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div className='SMEform__productDetailsDesktop'>
        <ProductDetails productData={productData} schema={schema} />
      </div>
    </div>
  )
}

export default connect((state: FsCore.State, props: any) => {
  return {
    brand: brand(state),
    productData: getComponentById(state, props.sys.id).productData,
    ...props,
  }
})(SMEzemplerForm)
